import * as React from 'react';
import { FC, useCallback } from 'react';

import { useIntl } from 'react-intl';
import { Linking } from 'react-native';

import { ISanityImage } from '@rbi-ctg/menu';
import Picture from 'components/picture';
import { Card } from 'components/ucl/card';
import { useNavigation } from 'hooks/navigation/use-navigation';
import {
  ClickEventComponentNames,
  CustomEventNames,
  EventTypes,
  logButtonClick,
  logEvent,
} from 'state/amplitude';
import { EventName, emitEvent } from 'utils/event-hub';
import { isExternalLink } from 'utils/is-external-link';

import useTerms from '../terms/terms.hook';

import { IMarketingCardProps } from './types';

/**
 *
 * MarketingCard is the Sanity Data component that wraps the Component-library Card component
 *
 */
export const MarketingCard: FC<React.PropsWithChildren<IMarketingCardProps>> = ({
  card,
  onShowTermsModal,
  index,
}) => {
  const { linkTo } = useNavigation();
  const { formatMessage } = useIntl();
  const loggedCards = React.useRef<string[]>([]);

  // Colors
  const isCustom = card.hasCustomColors;
  const variant = isCustom ? 'campaign' : 'default';
  const buttonTextColor = card.buttonTextColor?.hex ?? undefined;
  const buttonBgColor = card.buttonBackgroundColor?.hex ?? undefined;
  const bgColor = card.backgroundColor?.hex ?? undefined;
  const textColor = card.textColor?.hex ?? undefined;
  // Call to action
  const callToAction = (card.callToAction?.actionText?.locale ?? '') as string;
  const href = (card.callToAction?.actionUrl?.locale ?? '') as string;
  // Content
  const header = card.header?.locale ?? '';
  const description = card.description?.locale ?? '';
  const sanityImage = card.image?.locale?.app as ISanityImage;
  // If content team doesn't write an image description try to use the CTA as description of the image link to ensure context for accessibility users.
  const imageAlt = (card.image?.locale?.imageDescription ?? callToAction) as string;

  const onImageLoaded = useCallback(() => {
    emitEvent(EventName.MARKETING_IMAGE_LOADED);

    // Log to Amplitude
    if (!loggedCards.current.includes(card._id)) {
      loggedCards.current.push(card._id);

      const eventAttributes = {
        cardSource: 'Sanity',
        cardId: card._id,
        cardTitle: header,
      };
      logEvent(CustomEventNames.MARKETING_CARD_RENDERED, EventTypes.Other, eventAttributes);
    }
  }, [card._id, header]);

  const image = sanityImage && (
    <Picture
      image={sanityImage}
      borderTopRadius={16}
      lockedAspectRatio
      alt={imageAlt}
      onImageLoaded={onImageLoaded}
    />
  );

  const logDataEvent = useCallback(() => {
    logButtonClick(
      {
        attributes: {
          component: ClickEventComponentNames.MARKETING_TILE,
          componentId: card._id,
          name: header,
          position: `Tile ${index + 1}`,
        },
      },
      { logDuplicateClickEvent: true }
    );

    const eventAttributes = {
      'Marketing Card ID': card._id,
      'Marketing Card URL': href,
      sanityId: card._id,
      marketingCardUrl: href,
      marketingTileName: header,
    };
    // log the amplitude event
    logEvent(CustomEventNames.MARKETING_TILE_CLICK, EventTypes.Navigation, eventAttributes);
  }, [card._id, header, href, index]);

  const onPress = useCallback(() => {
    logDataEvent();
    // Prevent navigating to an empty url
    if (!href) {
      return;
    }
    if (isExternalLink(href)) {
      Linking.openURL(href);
    } else {
      linkTo(href);
    }
  }, [href, linkTo, logDataEvent]);

  // Terms
  const hasTerms = card.hasTerms;
  // Fallback to "terms and conditions" localized
  const termsAccessibilityLabel = formatMessage(
    { id: 'termsAccessibilityLabel' },
    { cardTitle: header }
  );
  const termsButtonText = (card.termsButton?.actionText?.locale ??
    formatMessage({ id: 'termsConditions' })) as string;
  const termsButtonUrl = (card.termsButton?.actionUrl?.locale ?? '') as string;
  const termsLinkHref = hasTerms ? termsButtonUrl : undefined;
  const termsLinkText = hasTerms ? termsButtonText : undefined;
  const termsText = card.termsText?.localeRaw;

  const onPressTermsLink = useTerms({
    onShowTermsModal,
    termsButtonText,
    termsButtonUrl,
    termsText,
  });

  return (
    <Card
      href={href}
      variant={variant}
      header={header}
      description={description}
      image={image}
      callToAction={callToAction}
      onPress={onPress}
      buttonTextColor={buttonTextColor}
      buttonBgColor={buttonBgColor}
      bgColor={bgColor}
      textColor={textColor}
      // Terms
      termsAccessibilityLabel={termsAccessibilityLabel}
      termsLinkHref={termsLinkHref}
      termsLinkText={termsLinkText}
      onPressTermsLink={onPressTermsLink as any}
    />
  );
};

export * from './types';
